import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//NPM PACKAGES
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { split, round, uniqBy } from "lodash";

//MATERIAL UI COMPONENTS
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { styled } from "@mui/material/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

//core components
import ShippingDetails from "components/ShippingDetails/index";
import { getExtraDetails, getOrder, getCarriers, getWarehouses, updateOrder } from "redux/action/order.action";
import { orderShippingStatuses } from "assets/constants/constants";
import { returnOrderStatus } from "assets/constants/constants";
import { ItemLists } from "./ItemLists";
import axios from "../../config/axios";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  "&.MuiTableCell-body": {
    fontSize: 14
  }
}));

const ReturnOrderDetails = () => {
  //CONSTANTS

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const statuses = [
    {
      value: "processing",
      label: t("table.processing")
    },
    {
      value: "confirmed",
      label: t("table.confirmed")
    },
    {
      value: "completed",
      label: t("table.completed")
    },
    {
      value: "cancelled",
      label: t("table.cancelled")
    }
  ];
  const params = useParams();
  const ORDER_STATE = useSelector(state => state.order);
  const [selectedReturnDate, setSelectedReturnDate] = React.useState(null);
  const [selectedReturnTime, setSelectedReturnTime] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const [carrier, setCarrier] = React.useState("");
  const [warehouse, setWarehouse] = React.useState("");
  const [oneTimeValue, setOneTimeValue] = useState(null);
  const [notes, setNotes] = React.useState("");
  const [updatedOrder, setUpdatedOrder] = React.useState({});
  const [showComponent, setShowComponent] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [hooksDependency, setHookDependency] = React.useState(true);
  const [hooksDependency2, setHookDependency2] = React.useState({
    random1: true,
    random2: true,
    random3: true,
    random4: true
  });
  const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
  const [costEstimate, setCostEstimate] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costFetched, setCostFetched] = useState(false);
  //FUNCTIONS
  useEffect(() => {
    if (hooksDependency) {
      let { id } = params;
      const handleSuccess = ({ order }) => {
        if (order.status === "confirmed" && !order.carrier_id) {
          enqueueSnackbar(t("messages.carrierWarning"), { variant: "warning" });
        }
      };
      dispatch(getOrder({ id, type: "return-order" }, handleSuccess));
      setShowComponent(true);
      if (!ORDER_STATE.extraDetails) {
        dispatch(getExtraDetails());
      }
      if (!ORDER_STATE.carriers || !ORDER_STATE.warehouses) {
        dispatch(getCarriers());
        dispatch(getWarehouses());
      }
      setHookDependency(false);
    }
  }, [ORDER_STATE, dispatch, params, hooksDependency, enqueueSnackbar, t]); //[] ONLY TO REMOVE WARNINGS

  useEffect(() => {
    if (showComponent && ORDER_STATE.order && !status && hooksDependency2.random1) {
      let date = moment(split(ORDER_STATE?.order.return_date, "::")[0]).format("YYYY-MM-DD");
      let time = split(ORDER_STATE?.order?.return_date, "::")[1];
      setSelectedReturnDate(date);
      setSelectedReturnTime(time);

      setStatus(ORDER_STATE.order.status);
      setOneTimeValue(ORDER_STATE.order.amount.one_time.value);
      setNotes(ORDER_STATE.order.notes);
      setHookDependency2({ ...hooksDependency2, random1: false });
    }
    if (showComponent && ORDER_STATE.order && ORDER_STATE.carriers && !carrier && hooksDependency2.random2) {
      setCarrier(ORDER_STATE.order.carrier_id);
      setHookDependency2({ ...hooksDependency2, random2: false });
    }
    if (showComponent && ORDER_STATE.order && ORDER_STATE.warehouses && !warehouse && hooksDependency2.random3) {
      setWarehouse(ORDER_STATE.order.warehouse_id);
      setHookDependency2({ ...hooksDependency2, random3: false });
    }
    if (showComponent && ORDER_STATE.order && ORDER_STATE.order.return_date && !selectedReturnDate && hooksDependency2.random4) {
      setSelectedReturnDate(moment(split(ORDER_STATE?.order.return_date, "::")[0]).format("YYYY-MM-DD"));
      setHookDependency2({ ...hooksDependency2, random4: false });
    }
  }, [ORDER_STATE, carrier, hooksDependency2, selectedReturnDate, showComponent, status, warehouse]); // [ORDER_STATE] ADDED FOR REMOVE WARNINGS

  // Fetch estimated return cost when order loads
  useEffect(() => {
    if (ORDER_STATE.order && !costFetched) {
      const { id } = params;
      setCostFetched(true);
      setCostLoading(true);
      axios
        .get(`/admin/return-orders/${id}/estimate-cost`)
        .then(res => {
          setCostEstimate(res.data.data);
        })
        .catch(() => {
          setCostEstimate(null);
        })
        .finally(() => setCostLoading(false));
    }
  }, [ORDER_STATE.order, costFetched, params]);

  const handleupdateOrderSuccess = () => {
    enqueueSnackbar(t("messages.orderUpdated"), { variant: "success" });
    if (updatedOrder?.status === "confirmed" && !carrier) {
      enqueueSnackbar(t("messages.carrierWarning"), { variant: "warning" });
    }
    setUpdatedOrder({});
  };
  const handleUpdateOrderFailure = ({ error }) => {
    enqueueSnackbar(error, { variant: "error" });
  };
  const HandleSubmit = event => {
    event.preventDefault();

    let order = { ...updatedOrder };
    if (order.date && order.time) {
      order.return_date = `${order.date}::${order.time}`;
      delete order.date;
      delete order.time;
    } else if (order.date) {
      order.return_date = `${order.date}::${split(ORDER_STATE.order.return_date, "::")[1]}`;
      delete order.date;
    } else if (order.time) {
      order.return_date = `${split(ORDER_STATE.order.return_date, "::")[0]}::${order.time}`;
      delete order.time;
    }
    dispatch(updateOrder({ updatedOrder: order, order_id: ORDER_STATE.order._id, type: "return-order" }, handleupdateOrderSuccess, handleUpdateOrderFailure));
  };

  const handleReturnDateChange = event => {
    // Hoje ou datas futuras (antes, o próprio dia era recusado sem aviso)
    if (moment(event.target.value).isSameOrAfter(moment(), "day")) {
      setSelectedReturnDate(event.target.value);

      setUpdatedOrder({ ...updatedOrder, date: moment(event.target.value).format("YYYY-MM-DD") });
    } else {
      enqueueSnackbar("A data de devolução não pode ser no passado.", { variant: "warning" });
    }
  };
  const handleReturnTimeChange = event => {
    setSelectedReturnTime(event.target.value);
    setUpdatedOrder({ ...updatedOrder, time: event.target.value });
  };
  const onChangeOneTimeValue = value => {
    setOneTimeValue(value);
    setUpdatedOrder({ ...updatedOrder, one_time: { value: round(value, 2), currency: "€" } });
  };
  const onChangeCarrier = value => {
    setCarrier(value);
    setUpdatedOrder({ ...updatedOrder, carrier_id: value });
  };
  const onChangeWarehouse = value => {
    setWarehouse(value);
    setUpdatedOrder({ ...updatedOrder, warehouse_id: value });
  };
  const onnotesChange = event => {
    setNotes(event.target.value);
    setUpdatedOrder({ ...updatedOrder, notes: event.target.value });
  };

  const handleStatusChange = event => {
    setStatus(event.target.value);
    setUpdatedOrder({ ...updatedOrder, status: event.target.value });
  };

  return (
    <div>
      <h3 style={{ fontWeight: "600" }}>{t("returnOrderDetails.title")}</h3>

      {ORDER_STATE.fetchingOrderLoading ? (
        <div className="loading">
          <CircularProgress />
        </div>
      ) : (
        <div>
          {showComponent && ORDER_STATE.order && ORDER_STATE.extraDetails && ORDER_STATE.carriers && ORDER_STATE.warehouses && (
            <div>
              <div className="details">
                <div className="title">
                  {t("returnOrderDetails.returnOrder")} #{ORDER_STATE.order._id}
                </div>
                <div className="card">
                  <div className="col">
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.returnDate")}</div>
                      <div className="form-input">
                        <Grid>
                          <TextField
                            id="datetime-local"
                            label={t("returnOrderDetails.date")}
                            type="date"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true
                            }}
                            value={selectedReturnDate === null ? "" : selectedReturnDate}
                            onChange={handleReturnDateChange}
                          />
                        </Grid>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.returnTime")}</div>
                      <div className="form-input">
                        <FormControl variant="outlined">
                          <InputLabel id="demo-simple-select-outlined-label">{t("returnOrderDetails.time")}</InputLabel>
                          <Select
                            labelId="demo-simple-select-outlined-label"
                            id="demo-simple-select-outlined"
                            value={updatedOrder?.time ? updatedOrder.time : selectedReturnTime === null ? "" : selectedReturnTime}
                            onChange={handleReturnTimeChange}
                            label={t("returnOrderDetails.time")}
                          >
                            <MenuItem value="08h - 12h">{t("time_slot_morning", "08h - 12h")}</MenuItem>
                            <MenuItem value="13h - 17h">{t("time_slot_afternoon", "13h - 17h")}</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>

                    <div className="row">
                      <div className="label">{t("returnOrderDetails.status")}</div>
                      <div className="form-input">
                        <TextField
                          id="standard-select-status"
                          select
                          label={t("returnOrderDetails.select")}
                          value={status === null ? "" : status}
                          onChange={e => handleStatusChange(e)}
                          helperText={t("returnOrderDetails.pleaseSelectStatus")}
                          variant="outlined"
                        >
                          {statuses.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </div>
                    {ORDER_STATE.order?.signature_image_url && (
                      <div className="row">
                        <div className="label">{t("returnOrderDetails.signature", "Signature")}</div>
                        <div>
                          <img
                            src={ORDER_STATE.order.signature_image_url}
                            alt={t("returnOrderDetails.signatureAlt", "Customer signature")}
                            style={{ maxWidth: "300px", maxHeight: "150px", border: "1px solid #ddd", borderRadius: "4px", padding: "5px", cursor: "pointer" }}
                            onClick={() => setSignatureDialogOpen(true)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.location")}</div>
                      <div>
                        <div className="row">
                          <div className="label">{t("returnOrderDetails.address")}</div>
                          <div>{ORDER_STATE.order.location.address}</div>
                        </div>
                        <div className="row">
                          <div className="label">{t("returnOrderDetails.floor")}</div>
                          <div>{ORDER_STATE.order.location.floor}</div>
                        </div>
                        <div className="row">
                          <div className="label">{t("returnOrderDetails.city")}</div>
                          <div>{ORDER_STATE.order.location.city}</div>
                        </div>
                        <div className="row">
                          <div className="label">{t("returnOrderDetails.zipcode")}</div>
                          <div>{ORDER_STATE.order.location.zipcode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.notes")}</div>
                      <div className="form-input">
                        <TextField
                          id="outlined-multiline-static"
                          label={t("returnOrderDetails.multiline")}
                          multiline
                          rows={4}
                          onChange={e => onnotesChange(e)}
                          value={notes || ""}
                          variant="outlined"
                          fullWidth={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="save">
                  {ORDER_STATE.updateOrderLoading ? (
                    <Button variant="contained" color="primary" disabled={true}>
                      {t("common.loading")}
                    </Button>
                  ) : (
                    <Button variant="contained" color="primary" onClick={e => HandleSubmit(e)} disabled={Object.keys(updatedOrder).length <= 0 ? true : false}>
                      {t("common.save")}
                    </Button>
                  )}
                </div>
              </div>

              <div className="details">
                <div className="title">{t("returnOrderDetails.shippingDetails")}</div>
                <div className="card">
                  <div className="col">
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.shippingAmount")}</div>
                      <div className="form-input">
                        <FormControl fullWidth variant="outlined">
                          <InputLabel htmlFor="outlined-adornment-amount">{t("returnOrderDetails.amount")}</InputLabel>
                          <OutlinedInput
                            id="outlined-adornment-amount"
                            value={oneTimeValue === null ? "" : round(oneTimeValue, 2)}
                            onChange={e => {
                              onChangeOneTimeValue(e.target.value);
                            }}
                            disabled
                            startAdornment={<InputAdornment position="start">€</InputAdornment>}
                            labelWidth={60}
                          />
                        </FormControl>
                      </div>
                    </div>
                    {(() => {
                      let feeDetails = ORDER_STATE.order.one_time_fee_details;
                      if (typeof feeDetails === 'string') {
                        try {
                          feeDetails = JSON.parse(feeDetails);
                        } catch (e) {
                          feeDetails = null;
                        }
                      }
                      return feeDetails && feeDetails.items && Array.isArray(feeDetails.items) && feeDetails.items.length > 0 && (
                        <div style={{ marginTop: "20px" }}>
                          <div className="label" style={{ marginBottom: "16px", fontWeight: "600", fontSize: "1.1em" }}>
                            {t("orderDetails.carrierCharges", "Cobranças Adicionais")}
                          </div>
                          <div style={{ border: "1px solid #e0e0e0", borderRadius: "4px", padding: "0" }}>
                            {feeDetails.items.map((item, index) => {
                              const itemName = item.namePt || item.name || item.nameEn || "-";
                              const itemDescription = item.descriptionPt || item.description || "";
                              const quantity = item.quantity || 1;
                              const unitPrice = item.price || item.unitPrice || 0;
                              const subtotal = item.subtotal || (quantity * unitPrice);
                              const currency = item.currency || "€";
                              
                              return (
                                <div 
                                  key={index}
                                  style={{
                                    padding: "16px",
                                    borderBottom: index < feeDetails.items.length - 1 ? "1px solid #e0e0e0" : "none",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start"
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: "600", marginBottom: "4px", fontSize: "0.95em" }}>
                                      {itemName}
                                    </div>
                                    <div style={{ fontSize: "0.85em", color: "#666", marginTop: "2px" }}>
                                      {quantity} × {round(unitPrice, 2)}{currency}
                                    </div>
                                    {itemDescription && (
                                      <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                                        {itemDescription}
                                      </div>
                                    )}
                                    {item.metadata && (
                                      <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
                                        {item.metadata.flat_pickup_cost && (
                                          <div>{t("orderDetails.flatPickupCost")}: {round(item.metadata.flat_pickup_cost, 2)}€</div>
                                        )}
                                        {item.metadata.extra_distance_cost && (
                                          <div>{t("orderDetails.extraDistanceCost")}: {round(item.metadata.extra_distance_cost, 2)}€</div>
                                        )}
                                        {item.metadata.floor_number && item.metadata.cost_per_floor && (
                                          <div>{t("orderDetails.floorNumber")}: {item.metadata.floor_number} × {round(item.metadata.cost_per_floor, 2)}€</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ fontWeight: "600", fontSize: "0.95em", marginLeft: "16px" }}>
                                    {round(subtotal, 2)}{currency}
                                  </div>
                                </div>
                              );
                            })}
                            <div style={{ padding: "16px", borderTop: "1px solid #e0e0e0", backgroundColor: "#f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ fontWeight: "600", fontSize: "1em" }}>
                                {t("orderDetails.total", "Total")}
                              </div>
                              <div style={{ fontWeight: "700", fontSize: "1.1em" }}>
                                {round(feeDetails.total || ORDER_STATE.order.amount.one_time.value, 2)}{ORDER_STATE.order.amount.one_time.currency || "€"}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {/* Estimated cost panel */}
                    <div style={{ marginTop: "16px", marginBottom: "8px" }}>
                      {costLoading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px" }}>
                          <CircularProgress size={16} />
                          <span style={{ fontSize: "0.9em", color: "#666" }}>A calcular estimativa...</span>
                        </div>
                      ) : costEstimate ? (
                        <div style={{
                          backgroundColor: "#e8f4fd",
                          borderRadius: "6px",
                          padding: "12px 16px",
                          border: "1px solid #b3d9f7"
                        }}>
                          <div style={{ fontWeight: "600", marginBottom: "10px", fontSize: "0.9em", color: "#1565c0" }}>
                            Custo estimado de devolução
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88em", marginBottom: "4px", color: "#333" }}>
                            <span>Transporte</span>
                            <span>{round(costEstimate.extra_detail_cost || 0, 2)}€</span>
                          </div>
                          {(costEstimate.extra_long_distance_cost > 0) && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88em", marginBottom: "4px", color: "#333" }}>
                              <span>Distância extra ({round(costEstimate.chargeable_distance_between_customer_and_warehouse || 0, 0)} km)</span>
                              <span>{round(costEstimate.extra_long_distance_cost, 2)}€</span>
                            </div>
                          )}
                          {costEstimate.free_delivery_applied && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88em", marginBottom: "4px", color: "#2e7d32" }}>
                              <span>Entrega gratuita aplicada</span>
                              <span>✓</span>
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700", borderTop: "1px solid #b3d9f7", paddingTop: "8px", marginTop: "6px", fontSize: "0.95em" }}>
                            <span>Total estimado (c/ IVA)</span>
                            <span style={{ color: "#1565c0" }}>{round((costEstimate.extra_detail_cost || 0) + (costEstimate.extra_long_distance_cost || 0), 2)}€</span>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {ORDER_STATE.order.stripe_shipping_details && (
                      <div className="row">
                        <div className="label">{t("returnOrderDetails.shippingStatus")}</div>
                        <div>
                          <Chip
                            label={orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.name}
                            variant="outlined"
                            style={{
                              border: "1px solid",
                              borderColor: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.borderColor,
                              backgroundColor: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.backgroundColor,
                              borderRadius: "5px",
                              fontWeight: "bold",
                              color: orderShippingStatuses[ORDER_STATE.order.stripe_shipping_details.p_intent_status]?.color
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="label">{t("returnOrderDetails.carrier")}</div>
                      <div className="form-input">
                        <div>
                          <TextField
                            id="standard-select-status"
                            select
                            fullWidth={true}
                            label={t("returnOrderDetails.select")}
                            value={carrier === null ? "" : carrier}
                            onChange={e => onChangeCarrier(e.target.value)}
                            helperText={t("returnOrderDetails.pleaseSelectCarrier")}
                            variant="outlined"
                          >
                            {ORDER_STATE.carriers.map(option => (
                              <MenuItem key={option._id} value={option._id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="label">{t("returnOrderDetails.warehouse")}</div>
                      <div className="form-input">
                        <div>
                          <TextField
                            id="standard-select-status"
                            select
                            fullWidth={true}
                            label={t("returnOrderDetails.select")}
                            value={warehouse === null ? "" : warehouse}
                            onChange={e => onChangeWarehouse(e.target.value)}
                            helperText={t("returnOrderDetails.pleaseSelectWarehouse")}
                            variant="outlined"
                          >
                            {ORDER_STATE.warehouses.map(option => (
                              <MenuItem key={option._id} value={option._id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <ShippingDetails listOfExtraDetails={ORDER_STATE.order.list_of_extra_details} />
                  </div>
                </div>
              </div>

              <div className="details">
                <div className="title">{t("returnOrderDetails.customerInformation")}</div>

                <div className="col">
                  <div className="row">
                    <div className="label">{t("table.id")}</div>
                    <Link to={`/admin/customers/${ORDER_STATE.order.user_id._id}`}>
                      <div style={{ fontWeight: "400" }}>{ORDER_STATE.order.user_id._id}</div>
                    </Link>
                  </div>
                  <div className="row">
                    <div className="label">{t("returnOrderDetails.name")}</div>

                    <div>{ORDER_STATE.order.user_id.name}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("returnOrderDetails.email")}</div>
                    <div>{ORDER_STATE.order.user_id.email}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("returnOrderDetails.phoneNumber")}</div>
                    <div>{ORDER_STATE.order.user_id.phone}</div>
                  </div>
                  <div className="row">
                    <div className="label">{t("returnOrderDetails.nifNumber")}</div>
                    <div>{ORDER_STATE.order.user_id.nif}</div>
                  </div>
                </div>
              </div>
              <div className="details">
                <div className="title">{t("returnOrderDetails.items")}</div>
                <div className="table">
                  <TableContainer component={Paper}>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell />
                          <StyledTableCell>{t("table.id")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.contentName")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.quantity")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.size", "Size (m²)")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.picture", "Picture")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.defects", "Defects")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.productId")}</StyledTableCell>
                          <StyledTableCell align="center">{t("returnOrderDetails.orderId")}</StyledTableCell>
                          {ORDER_STATE.order.status === returnOrderStatus.processing && <StyledTableCell align="center">{t("returnOrderDetails.action")}</StyledTableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uniqBy(ORDER_STATE.order.items || [], '_id').map(item => (
                          <ItemLists key={item._id} item={item} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="lg">
        <DialogContent style={{ position: "relative", padding: 0 }}>
          <IconButton
            onClick={() => setSignatureDialogOpen(false)}
            style={{ position: "absolute", right: 8, top: 8, backgroundColor: "rgba(255, 255, 255, 0.9)", zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {ORDER_STATE.order?.signature_image_url && (
            <img src={ORDER_STATE.order.signature_image_url} alt={t("returnOrderDetails.signatureAlt", "Customer signature")} style={{ width: "100%", display: "block" }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturnOrderDetails;
